import { fastClone } from "core/utils/clone";
import { upgradePropertyConfig } from "./upgradePropertyConfig";
import type { ItemWithPropertiesType } from "./ItemKindConstants";
import type { PropertyPaneConfig } from "legacy/constants/PropertyControlConstants";
import type { WidgetTypes } from "legacy/constants/WidgetConstants";

/**
 * This map contains the property pane config for items of all kinds other than widget.
 * For widgets, we extract the property pane config from the widget registry, so there is
 * no need to add that information here.
 */
const propertyPaneConfigsRegistry = new Map<
  ItemWithPropertiesType,
  readonly PropertyPaneConfig[]
>();

export function getItemPropertyPaneConfig(
  type: ItemWithPropertiesType,
): readonly PropertyPaneConfig[] {
  const config =
    propertyPaneConfigsRegistry.get(type) ??
    v2PropertyPaneConfigsRegistry.get(type);
  // we have to fetch from v2 map as fallback since some widgets have been reorganized and thus the v1 map is not populated

  if (!config) {
    console.error(`Could not find property pane config for type ${type}`);
    return [];
  }
  return config;
}

// TODO (ALEX) - once we are done reorganizing the configs, delete the old code and rename it so we don't have "v2"
const v2PropertyPaneConfigsRegistry = new Map<
  ItemWithPropertiesType,
  readonly PropertyPaneConfig[]
>();

export function getItemV2PropertyPaneConfig(
  type: ItemWithPropertiesType,
): readonly PropertyPaneConfig[] {
  const existingConfig = v2PropertyPaneConfigsRegistry.get(type);
  if (existingConfig) {
    return existingConfig;
  }

  const v1Config = propertyPaneConfigsRegistry.get(type) ?? [];

  const v2Config = upgradePropertyConfig(type as WidgetTypes, v1Config);
  v2PropertyPaneConfigsRegistry.set(type, v2Config);
  return v2Config;
}

export function registerPropertyPaneConfig(
  itemType: ItemWithPropertiesType,
  config: PropertyPaneConfig[],
) {
  propertyPaneConfigsRegistry.set(itemType, Object.freeze(fastClone(config)));
}

export function registerV2PropertyPaneConfig(
  itemType: ItemWithPropertiesType,
  config: PropertyPaneConfig[],
) {
  v2PropertyPaneConfigsRegistry.set(itemType, Object.freeze(fastClone(config)));
}
